import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import FinalCTA from '@/components/home/FinalCTA'

const lineV = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.05 + i * 0.1 },
  }),
}

const capabilities = [
  {
    num: '01',
    title: 'Technology Leadership',
    area: 'Engineering & Architecture',
    desc: 'CTOs, VPs of Engineering, Site Heads, Distinguished Engineers, and Technical Fellows. We place the leaders who define technical direction and build the engineering culture.',
    roles: ['Chief Technology Officer', 'VP of Engineering', 'Site / Country Head — Technology', 'Principal Engineer / Technical Fellow', 'Head of Engineering Excellence'],
  },
  {
    num: '02',
    title: 'Data Science & AI',
    area: 'Intelligence & Machine Learning',
    desc: 'Heads of Data Science, ML Architects, Chief Data Officers, and Principal Data Engineers — the specialists who build and lead world-class AI and data practices.',
    roles: ['Chief Data Officer', 'Head of Data Science', 'ML Engineering Lead', 'Principal Data Scientist', 'Director of AI Research'],
  },
  {
    num: '03',
    title: 'Product Leadership',
    area: 'Strategy & Direction',
    desc: 'VPs of Product, Group Product Managers, and Directors who shape global product direction — balancing local execution with global stakeholder alignment.',
    roles: ['VP of Product', 'Director of Product Management', 'Group Product Manager', 'Head of Product — Regional', 'Principal Product Manager'],
  },
  {
    num: '04',
    title: 'Platform & Infrastructure',
    area: 'Cloud & DevOps',
    desc: 'Cloud Architecture leaders, DevOps Heads, SRE Leads, and Platform Engineering Directors who build the reliability and scale layer every GCC depends on.',
    roles: ['Head of Platform Engineering', 'Director of Infrastructure', 'VP of Site Reliability', 'Cloud Architecture Lead', 'Head of DevOps / Delivery'],
  },
  {
    num: '05',
    title: 'Founding GCC Teams',
    area: 'Centre Inception',
    desc: 'The most critical engagement we run. Full leadership cohort placement for centres standing up their global presence — from the first hire to the first 15.',
    roles: ['Founding Site Leader', 'VP Engineering + VP Data', 'Head of Operations — GCC', 'Country HR Lead', 'Head of Finance & Legal'],
  },
  {
    num: '06',
    title: 'Digital & Design',
    area: 'Experience & Transformation',
    desc: 'Heads of UX, Design Directors, and Digital Transformation Leaders who bring global product experience and a user-first perspective to regional operations.',
    roles: ['Head of Design / UX', 'Digital Transformation Lead', 'Director of Customer Experience', 'Head of Design Systems', 'VP Digital Product'],
  },
  {
    num: '07',
    title: 'Analytics & Insights',
    area: 'Intelligence & Decision Science',
    desc: 'Business Intelligence Heads, Analytics Engineering Leads, and Decision Science Directors who make data actionable at scale.',
    roles: ['Head of Business Intelligence', 'Director of Analytics Engineering', 'Decision Science Lead', 'Head of Data Platform', 'Analytics Capability Lead'],
  },
  {
    num: '08',
    title: 'Security & Compliance',
    area: 'Risk & Governance',
    desc: 'CISOs, VP Security, and Heads of Risk and Compliance for regulated-industry GCCs navigating the evolving data and cybersecurity landscape.',
    roles: ['CISO', 'VP Information Security', 'Head of Risk & Compliance', 'Director of Cybersecurity', 'GRC Lead'],
  },
]

function CapabilityRow({ cap, index }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: index * 0.04 }}
      className="border-b border-[#E5E7EB]"
      data-testid={`capability-row-${index + 1}`}
    >
      <button
        className="w-full text-left py-7 flex items-center gap-6 md:gap-12 hover:bg-[#F6F7F9] transition-colors duration-200 px-2 group"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-[10px] font-body text-[#0A0A0A]/20 w-6 flex-shrink-0 font-semibold tracking-widest pt-0.5">
          {cap.num}
        </span>
        <div className="flex-1 flex flex-col md:flex-row md:items-center gap-2 md:gap-10">
          <h3 className="text-base md:text-lg font-heading font-bold text-[#0A0A0A] leading-tight tracking-tight min-w-[200px]">
            {cap.title}
          </h3>
          <p className="text-xs font-body text-[#0A0A0A]/35 hidden md:block">{cap.area}</p>
        </div>
        <span className={`text-[#0A0A0A]/30 text-lg leading-none transition-transform duration-300 flex-shrink-0 ${open ? 'rotate-45' : ''}`}>
          +
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-2 pb-8 pl-16 grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8">
              <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed">{cap.desc}</p>
              <div>
                <p className="text-[9px] tracking-[0.25em] uppercase text-[#0A0A0A]/25 font-body font-semibold mb-3">
                  Roles we place
                </p>
                <ul className="space-y-1.5">
                  {cap.roles.map((role) => (
                    <li key={role} className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-[#16365F] flex-shrink-0" />
                      <span className="text-xs font-body text-[#0A0A0A]/45">{role}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function CapabilitiesPage() {
  return (
    <main data-testid="capabilities-page">
      <Navbar />

      {/* Hero */}
      <section className="min-h-[60vh] bg-[#0A0A0A] flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-20 pt-36 relative overflow-hidden">
        <motion.div initial="hidden" animate="visible" className="max-w-7xl relative z-10">
          <div className="overflow-hidden mb-6">
            <motion.p custom={0} variants={lineV}
              className="text-[10px] tracking-[0.38em] uppercase font-body font-semibold text-white/30">
              Where We Search
            </motion.p>
          </div>
          <h1 className="mb-8">
            <div className="overflow-hidden">
              <motion.div custom={1} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-black tracking-[-0.03em] leading-none text-white">
                Eight capability areas.
              </motion.div>
            </div>
            <div className="overflow-hidden">
              <motion.div custom={2} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-light tracking-[-0.03em] leading-none text-white/35">
                One standard.
              </motion.div>
            </div>
          </h1>
          <div className="overflow-hidden">
            <motion.p custom={3} variants={lineV}
              className="text-base md:text-lg text-white/45 max-w-xl font-body leading-relaxed">
              From founding leadership to specialist individual contributors — every search is conducted with the same depth and structural rigour, regardless of seniority.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* Capabilities list */}
      <section
        data-testid="capabilities-expanded-section"
        className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16"
          >
            <h2 className="text-[clamp(2rem,4vw,4rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
              Capability index
            </h2>
            <p className="text-xs font-body text-[#0A0A0A]/40 max-w-xs leading-relaxed">
              Click any area to expand roles and search scope. All searches are retained and run to completion.
            </p>
          </motion.div>

          <div className="border-t border-[#E5E7EB]">
            {capabilities.map((cap, i) => (
              <CapabilityRow key={cap.num} cap={cap} index={i} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 flex flex-wrap items-center gap-8"
          >
            <Link
              to="/contact"
              data-testid="capabilities-cta"
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white text-[10px] font-body font-semibold tracking-[0.18em] uppercase px-8 py-4 hover:bg-[#0B1F3A] transition-colors duration-300"
            >
              Discuss your mandate <ArrowRight size={13} />
            </Link>
            <Link
              to="/services"
              className="text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5 hover:text-[#0B1F3A] hover:border-[#0B1F3A] transition-colors"
            >
              View our services <ArrowRight size={11} className="inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  )
}
